import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';

import { isFreeTier } from '../../../../shared/subscriptions';
import { useLocation } from '../../../contexts/RouterContext';
import { window } from '../../../globals';
import { getCategoryLabel, permittedIssueCategoriesToView } from '../../../helpers/issuesV2';
import * as IssuesV2Store from '../../../redux/issuesV2';
import Subnav from '../../generic/Subnav';
import RequireIssueOverview from '../../issueOverview/RequireIssueOverview';
import { issueOverviewFeature } from '../../settings/billing/features/consts';
import useCategorySummary from '../IssuesInbox/hooks/useCategorySummary';

/**
 * GlobalIssuesSubnav is the subnav for the global issues inbox.
 * It is rendered way up in the IssuesContainer component,
 * so that it can extend the full width of the page.
 */
const GlobalIssuesSubnav: VoidFunctionComponent = () => {
  const { pathname } = useLocation();
  const { isLoading } = useSelector(IssuesV2Store.selectors.getAsyncStatusCategorySummary);
  const scope = useSelector(IssuesV2Store.selectors.getScope);
  const categorySummary = useCategorySummary(scope);

  //  Recalculate the permitted categories only if user permissions actually change.
  const permittedCategories = useMemo(permittedIssueCategoriesToView, [window.user?.permissions]);

  return (
    <Subnav activeKey={pathname}>
      <RequireIssueOverview>
        <Subnav.PaidFeatureItem
          to="/issues/overview"
          navKey="/issues/overview"
          isRestricted={isFreeTier({
            plan: window.user?.organization?.stripe_plan,
            stripePriceLimit: window.user?.organization?.stripePriceLimit,
          })}
          modalFocusFeature={issueOverviewFeature}
          testId="global-issues-subnav-item--overview"
        >
          Overview
        </Subnav.PaidFeatureItem>
      </RequireIssueOverview>
      {permittedCategories.map((category) => (
        <Subnav.ItemWithCount
          key={category}
          to={`/issues/${category}`}
          navKey={`/issues/${category}`}
          isLoading={isLoading || !categorySummary}
          count={categorySummary?.[category] || 0}
          testId={`global-issues-subnav-item--${category}`}
        >
          {getCategoryLabel(category)}
        </Subnav.ItemWithCount>
      ))}
    </Subnav>
  );
};

export default GlobalIssuesSubnav;
